import React, { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';

import Textarea from '@cloudscape-design/components/textarea';
import SpaceBetween from '@cloudscape-design/components/space-between';

import { useTranslation } from 'react-i18next';
import { useNotificationContext } from './NotificationContext';
import { useFeedbackModal } from './hooks/useFeedbackModal';
import { useFeedbackMutation } from 'src/api/mutation/useFeedbackMutation';
import { useAwsRum } from 'src/provider/AwsRumProvider';
import './global-feedback-modal.scss';

export const GlobalFeedbackModal: React.FC = () => {
  const { t } = useTranslation();
  const { recordError } = useAwsRum();
  const { addNotification } = useNotificationContext();
  const feedbackMutation = useFeedbackMutation();
  const { shouldShow, hideFeedback, markAsSubmitted, markAsSkipped } = useFeedbackModal();
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');

  const isCommentTooLong = comment.length > 500;
  const isInvalid =
    !rating || (rating <= 3 && !comment.trim()) || isCommentTooLong;

  const handleSubmit = async () => {
    if (isInvalid) return;

    try {
      await feedbackMutation.mutateAsync({
        pageName: window.location.pathname,
        rating,
        comment: comment.trim(),
      });

      markAsSubmitted();
      addNotification({
        type: 'success',
        content: t('feedback_success_message'),
        id: 'feedback-success',
      });
    } catch (error) {
      recordError(error);
      addNotification({
        type: 'error',
        content: t('feedback_failure_message'),
        id: 'feedback-error',
      });
    }
  };

  return (
    <Modal
      visible={shouldShow}
      onDismiss={hideFeedback}
      header={t('help_improve_webmec_message')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={markAsSkipped}>
              {t('skip')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={feedbackMutation.isLoading}
              disabled={isInvalid}
            >
              {t('submit_feedback')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label={t('rate_overall_webmec_experience_message')}>
          <br />
          <div className="feedback-rating-container">
            {[
              'very_unsatisfied',
              'unsatisfied',
              'neutral',
              'satisfied',
              'very_satisfied',
            ].map((label, index) => {
              const ratingValue = index + 1;
              return (
                <div key={ratingValue} className="feedback-rating-item">
                  <button
                    type="button"
                    onClick={() => setRating(ratingValue)}
                    className={`feedback-rating-button ${
                      rating === ratingValue ? 'selected' : ''
                    }`}
                  >
                    {ratingValue}
                  </button>
                  <span className="feedback-rating-label">{t(label)}</span>
                </div>
              );
            })}
          </div>
        </FormField>

        <FormField
          label={
            <span>
              {t('feedback_additional_comments')}
              {rating &&
                (rating <= 3 ? (
                  <span className="feedback-required-text"> (*required)</span>
                ) : (
                  <span> (optional)</span>
                ))}
            </span>
          }
        >
          <SpaceBetween size="xs">
            <Textarea
              value={comment}
              onChange={({ detail }) => setComment(detail.value)}
              placeholder={t('feedback_comment_placeholder')}
              rows={3}
              invalid={isCommentTooLong}
            />
            <Box fontSize="body-s" color="text-status-info">
              {comment.length} {t('characters_entered')}.
            </Box>
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
